// src/app/components/music-player/MusicPlayer.tsx

'use client';

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import Image from 'next/image';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import styles from './MusicPlayer.module.scss';
import useSounds from '@/hooks/useSounds';
import useMixpanelTracking from '@/hooks/useMixpanelTracking';
import { fetchMusicData } from 'app/actions/music';

type Track = { name: string; url: string };
type Genre = { name: string };

const MusicPlayer: React.FC = () => {
  const [playerImage, setPlayerImage] = useState('/player/player-1.webp');
  const [playerImage5] = useState('/player/player-5.webp');
  const [genres, setGenres] = useState<Genre[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [currentGenreIndex, setCurrentGenreIndex] = useState(0);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [displayMessage, setDisplayMessage] = useState(
    'Chadify Radio by ChadAI - Press Play',
  );
  const [volume, setVolume] = useState(0.5);
  const [, setRotation] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const { playSound } = useSounds();
  const { track } = useMixpanelTracking();

  const formatTrackName = useMemo(
    () => (name: string) => name.replace(/_/g, ' ').replace('.mp3', ''),
    [],
  );

  // 1) load genres + first‐genre tracks on mount
  useEffect(() => {
    async function loadInitial() {
      try {
        const genreData = (await fetchMusicData()) as Genre[]; // cast!
        if (genreData.length === 0) return;

        // Sort genres to put "Stealth Synthwave" first
        const sortedGenres = [...genreData].sort((a, b) => {
          if (a.name === 'Stealth Synthwave') return -1;
          if (b.name === 'Stealth Synthwave') return 1;
          return a.name.localeCompare(b.name);
        });

        setGenres(sortedGenres);
        const firstGenre = sortedGenres[0].name;

        const trackData = (await fetchMusicData(firstGenre)) as Track[];
        setTracks(trackData);
        setIsInitialized(true);
      } catch (err) {
        console.error('Init load error:', err);
      }
    }
    void loadInitial(); // kill ESLint floating‐promise
  }, []);

  // 2) fetch tracks by genre
  const fetchTracksFor = useCallback(
    async (genreIndex: number) => {
      const genre = genres[genreIndex];
      if (!genre) return;
      try {
        const trackData = (await fetchMusicData(genre.name)) as Track[];
        setTracks(trackData);
        setCurrentTrackIndex(0);
        setIsInitialized(true);
      } catch (err) {
        console.error('Error fetching tracks:', err);
      }
    },
    [genres],
  );

  // 3) volume → rotation
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      setRotation(volume * 360);
    }
  }, [volume]);

  // 4) sync audio src & play/pause
  useEffect(() => {
    const player = audioRef.current;
    const trackObj = tracks[currentTrackIndex];
    if (!player || !trackObj) return;

    player.src = trackObj.url;
    if (isPlaying) {
      void player.play(); // kill ESLint
      setDisplayMessage(
        `${genres[currentGenreIndex]?.name} - ${formatTrackName(trackObj.name)}`,
      );
    }
  }, [
    currentTrackIndex,
    isPlaying,
    tracks,
    genres,
    currentGenreIndex,
    formatTrackName,
  ]);

  // common play/pause
  const playTrack = useCallback(() => {
    const player = audioRef.current;
    const cur = tracks[currentTrackIndex];
    if (!player || !cur) return;

    if (!isPlaying) {
      void player.play(); // kill ESLint
      setDisplayMessage(
        `${genres[currentGenreIndex]?.name} - ${formatTrackName(cur.name)}`,
      );
    } else {
      player.pause();
      setDisplayMessage('ChadAI Radio Mix - Press Play');
    }
    setIsPlaying((v) => !v);
  }, [
    isPlaying,
    tracks,
    currentTrackIndex,
    genres,
    currentGenreIndex,
    formatTrackName,
  ]);

  // button handlers with void on promises
  const handlePlayPausePointerDown = useCallback(() => {
    setPlayerImage('/player/player-2.webp');
    void track('Music - Button Pressed', { button: 'Play/Pause' });
  }, [track]);

  const handlePlayPausePointerUp = useCallback(() => {
    setPlayerImage('/player/player-1.webp');
    if (!isInitialized) {
      void fetchTracksFor(currentGenreIndex).then(() => playTrack());
    } else {
      playTrack();
    }
    playSound('musicPlayerButton');
  }, [isInitialized, currentGenreIndex, fetchTracksFor, playTrack, playSound]);

  const handleNextTrack = useCallback(() => {
    // Only move within the current genre
    const nextTrackIdx = (currentTrackIndex + 1) % tracks.length;
    setCurrentTrackIndex(nextTrackIdx);

    // Ensure playback continues
    const wasPlaying = isPlaying;
    if (wasPlaying) {
      const player = audioRef.current;
      if (player) {
        player.pause();
        const trackObj = tracks[nextTrackIdx];
        if (trackObj) {
          player.src = trackObj.url;
          void player.play();
          setDisplayMessage(
            `${genres[currentGenreIndex]?.name} - ${formatTrackName(trackObj.name)}`,
          );
        }
      }
    }
    return wasPlaying;
  }, [
    currentTrackIndex,
    tracks,
    isPlaying,
    genres,
    currentGenreIndex,
    formatTrackName,
  ]);

  const handleNextPointerDown = useCallback(() => {
    setPlayerImage('/player/player-4.webp');
    void track('Music - Button Pressed', { button: 'Next' });
  }, [track]);

  const handleNextPointerUp = useCallback(() => {
    setPlayerImage('/player/player-1.webp');
    handleNextTrack();
    playSound('musicPlayerButton');
  }, [handleNextTrack, playSound]);

  const handleGenreChange = useCallback(() => {
    const newGenreIdx = (currentGenreIndex + 1) % genres.length;
    setCurrentGenreIndex(newGenreIdx);

    // Store if we were playing
    const wasPlaying = isPlaying;

    // Set playing to false temporarily to prevent autoplay in useEffect
    if (wasPlaying) {
      setIsPlaying(false);
    }

    // Fetch tracks for the new genre
    void fetchTracksFor(newGenreIdx).then(() => {
      // Resume playback if we were playing
      if (wasPlaying) {
        setIsPlaying(true);
        const player = audioRef.current;
        if (player && tracks.length > 0) {
          const trackObj = tracks[0];
          player.src = trackObj.url;
          void player.play();
          setDisplayMessage(
            `${genres[newGenreIdx]?.name} - ${formatTrackName(trackObj.name)}`,
          );
        }
      }
    });

    return wasPlaying;
  }, [
    currentGenreIndex,
    genres.length,
    isPlaying,
    fetchTracksFor,
    formatTrackName,
    tracks,
  ]);

  const handleGenrePointerDown = useCallback(() => {
    setPlayerImage('/player/player-3.webp');
    void track('Music - Button Pressed', { button: 'Genre' });
  }, [track]);

  const handleGenrePointerUp = useCallback(() => {
    setPlayerImage('/player/player-1.webp');
    handleGenreChange();
    playSound('musicPlayerButton');
  }, [handleGenreChange, playSound]);

  // draggable dial → volume
  useEffect(() => {
    gsap.registerPlugin(Draggable);
    if (imgRef.current) {
      Draggable.create(imgRef.current, {
        type: 'rotation',
        inertia: true,
        onDrag(this: Draggable) {
          const rot = Math.max(0, Math.min(this.rotation, 360));
          setRotation(rot);
          setVolume(rot / 360);
        },
      });
    }
  }, []);

  return (
    <div className={styles.musicPlayer}>
      <Image
        src={playerImage}
        alt='Music Player'
        className={styles.playerImage}
        width={500}
        height={300}
        priority
      />
      <div className={styles.screen}>
        <div className={styles.marquee}>
          <div>{displayMessage}</div>
        </div>
      </div>
      <div
        className={styles.playPauseButton}
        onPointerDown={handlePlayPausePointerDown}
        onPointerUp={handlePlayPausePointerUp}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        role='button'
        tabIndex={0}
      />
      <div
        className={styles.nextButton}
        onPointerDown={handleNextPointerDown}
        onPointerUp={handleNextPointerUp}
        aria-label='Next Track'
        role='button'
        tabIndex={0}
      />
      <div
        className={styles.genreButton}
        onPointerDown={handleGenrePointerDown}
        onPointerUp={handleGenrePointerUp}
        aria-label='Next Genre'
        role='button'
        tabIndex={0}
      />
      <div className={styles.player5Image}>
        <Image
          ref={imgRef}
          src={playerImage5}
          alt='Dial'
          width={100}
          height={100}
        />
      </div>
      <audio ref={audioRef} />
    </div>
  );
};

export default MusicPlayer;
